// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Button, Tag } from "@aviary";
import type { IngredientFilterV2 } from "@shared/components/CatalogSearch/CatalogSearch";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";

import { CommonIngredients } from "../CommonIngredients/CommonIngredients";
import { IngredientSearch } from "../IngredientSearch/IngredientSearch";

import * as styles from "./ExcludeIngredients.styles";

interface Props {
  logFilterEvent: (entityName: string, entityType: string) => void;
  isContextual?: boolean;
}

const ExcludeIngredientsContent = ({ logFilterEvent, isContextual = false }: Props) => {
  const { t } = useTranslation();

  const { excludedIngredients, setExcludedIngredients } = useCatalogSearch();

  const isIngredientExcluded = ingredientId =>
    excludedIngredients.some(
      excludedIngredient => excludedIngredient?.searchAttributes?.id === ingredientId
    );

  const onExcludeIngredient = (ingredientId, ingredientName) => {
    const distanceFromTop = window?.scrollY;
    if (isIngredientExcluded(ingredientId)) {
      setExcludedIngredients([
        ...(excludedIngredients as IngredientFilterV2[])?.filter(
          excludedIngredient => excludedIngredient.searchAttributes.id !== ingredientId
        ),
      ]);
    } else {
      const ingredientToExclude: IngredientFilterV2 = {
        name: ingredientName,
        searchAttributes: {
          id: ingredientId,
        },
      };
      logFilterEvent(ingredientName, FilterType.EXCLUDE_INGREDIENT);
      setExcludedIngredients([...excludedIngredients, ingredientToExclude]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const onClearAllIngredients = () => {
    const distanceFromTop = window?.scrollY;
    setExcludedIngredients([]);
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderExcludedIngredients = () =>
    excludedIngredients.map(excludedIngredient => (
      <Tag
        css={styles.excludedTag}
        key={excludedIngredient?.name}
        text={excludedIngredient?.name}
        noEllipses
        handleClick={() =>
          onExcludeIngredient(excludedIngredient?.searchAttributes?.id, excludedIngredient?.name)
        }
      />
    ));

  const renderClearAllIngredients = () => {
    if (excludedIngredients.length === 0) {
      return null;
    }

    return (
      <div css={styles.clearAll}>
        <Button
          aria-label={t(l.sharedCommon.ClearAllSearchFilters)}
          isText
          isColor="info"
          onClick={onClearAllIngredients}
        >
          {t(l.sharedCommon.Clearall)}
        </Button>
      </div>
    );
  };

  return (
    <>
      <IngredientSearch
        isIngredientSelected={isIngredientExcluded}
        onSelectIngredient={onExcludeIngredient}
        isContextual={isContextual}
      />

      {renderExcludedIngredients()}

      {!isContextual && (
        <CommonIngredients
          isIngredientSelected={isIngredientExcluded}
          selectedIngredients={excludedIngredients}
          onSelectIngredient={onExcludeIngredient}
        />
      )}

      {renderClearAllIngredients()}
    </>
  );
};

export { ExcludeIngredientsContent };
